import React, { useEffect, useState } from 'react';
import { safeFetch } from '../../../App';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { Input, Textarea, Label } from '../../ui/Input';

const EMPTY_FORM = {
    displayName: '',
    bio: '',
    shareEntities: false,
    sharePhone: false,
    phoneNumber: '',
    shareWhatsapp: false,
    shareEmail: false,
    email: '',
};

function memberToForm(member) {
    if (!member) return EMPTY_FORM;
    return {
        displayName:   member.name || '',
        bio:           member.bio || '',
        shareEntities: !!member.share_entities,
        sharePhone:    !!member.share_phone,
        phoneNumber:   member.phone_number || '',
        shareWhatsapp: !!member.share_whatsapp,
        shareEmail:    !!member.share_email,
        email:         member.email || '',
    };
}

/**
 * Edit-your-zone-profile modal. Pre-populates the form from `initialMember`
 * each time the modal opens (so closing without saving doesn't leave stale
 * state for the next opening).
 */
export function EditProfileModal({
    isOpen,
    onClose,
    chatId,
    zone,
    initialMember,
    onSaved,
}) {
    const [form, setForm] = useState(() => memberToForm(initialMember));
    const [saving, setSaving] = useState(false);

    // Reset the form whenever the modal opens with new data
    useEffect(() => {
        if (isOpen) setForm(memberToForm(initialMember));
    }, [isOpen, initialMember]);

    const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-zone-member-profile',
                chatId,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: chatId,
                        zone_id: zone,
                        display_name: form.displayName,
                        personal_note: form.bio,
                        share_entities: form.shareEntities,
                        share_phone: form.sharePhone,
                        phone_number: form.phoneNumber,
                        share_whatsapp: form.shareWhatsapp,
                        share_email: form.shareEmail,
                        email: form.email,
                    }),
                },
            );
            if (!res.ok) {
                const txt = await res.text();
                console.error('Update profile failed:', txt);
                alert('עדכון הפרופיל נכשל');
                return;
            }
            alert('הפרופיל עודכן בהצלחה');
            onSaved?.();
            onClose();
        } catch (err) {
            console.error('Update profile error:', err);
            alert('שגיאה בעדכון הפרופיל');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="עריכת פרופיל"
            subtitle="עדכנו את השם, הביוגרפיה והגדרות השיתוף"
            size="md"
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={saving}>ביטול</Button>
                    <Button variant="primary" onClick={handleSave} loading={saving}>
                        שמור שינויים
                    </Button>
                </>
            }
        >
            <div className="space-y-4">
                <div className="space-y-1.5">
                    <Label>שם לתצוגה</Label>
                    <Input
                        type="text"
                        value={form.displayName}
                        onChange={(e) => update({ displayName: e.target.value })}
                        placeholder="הזינו את השם שיוצג"
                    />
                </div>

                <div className="space-y-1.5">
                    <Label>ביוגרפיה</Label>
                    <Textarea
                        value={form.bio}
                        onChange={(e) => update({ bio: e.target.value })}
                        placeholder="ספרו קצת על עצמכם..."
                        rows={4}
                    />
                </div>

                <CheckboxRow
                    title="שתפו את הישויות שלי"
                    description="הצג את העסקים/מפגשים שלכם לחברי הקהילה"
                    checked={form.shareEntities}
                    onChange={(v) => update({ shareEntities: v })}
                />

                <div className="border-t border-line-subtle pt-4 space-y-3">
                    <h4 className="font-bold text-ink text-base">פרטי התקשרות</h4>

                    <CheckboxRow
                        title="טלפון"
                        description="שתפו את מספר הטלפון שלכם"
                        checked={form.sharePhone}
                        onChange={(v) => update({ sharePhone: v })}
                    />
                    {form.sharePhone && (
                        <Input
                            type="tel"
                            value={form.phoneNumber}
                            onChange={(e) => update({ phoneNumber: e.target.value })}
                            placeholder="05X-XXX-XXXX"
                        />
                    )}

                    <CheckboxRow
                        title="זמין בוואטסאפ"
                        description="ניתן ליצור קשר דרך WhatsApp"
                        checked={form.shareWhatsapp}
                        onChange={(v) => update({ shareWhatsapp: v })}
                    />

                    <CheckboxRow
                        title="אימייל"
                        description="שתפו את כתובת המייל שלכם"
                        checked={form.shareEmail}
                        onChange={(v) => update({ shareEmail: v })}
                    />
                    {form.shareEmail && (
                        <Input
                            type="email"
                            value={form.email}
                            onChange={(e) => update({ email: e.target.value })}
                            placeholder="example@mail.com"
                        />
                    )}
                </div>
            </div>
        </Modal>
    );
}

function CheckboxRow({ title, description, checked, onChange }) {
    return (
        <label className="flex items-center gap-3 p-4 bg-surface-muted rounded-lg border border-line-subtle hover:bg-surface-sunken transition-colors cursor-pointer">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange?.(e.target.checked)}
                className="w-5 h-5 rounded border-2 border-brand-500 text-brand-500 focus:ring-2 focus:ring-brand-400/30"
            />
            <div className="flex-1">
                <div className="font-bold text-ink">{title}</div>
                <div className="text-sm text-ink-muted">{description}</div>
            </div>
        </label>
    );
}

export default EditProfileModal;
